import React, { Dispatch, SetStateAction, useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import MatchCard from './MatchCard';
import { ScheduleMatch } from 'src/services/tournaments/getTournamentSchedule';
import { KnockoutStage, KnockoutMatch } from 'src/services/tournaments/getTournamentKnockouts';
import { Match } from 'src/views/pages/tournaments/components/Knockouts/types';

interface TournamentBracketProps {
  stages: KnockoutStage[];
  setSelectedMatch: Dispatch<SetStateAction<ScheduleMatch>>;
  setOpenEditMatch: Dispatch<SetStateAction<boolean>>;
  loadMatches: () => Promise<void>;
}

const MATCH_CARD_HEIGHT = 80;
const MATCH_VERTICAL_SPACING = 60;
const HEADER_HEIGHT = 60;
const COLUMN_WIDTH = 300;
const COLUMN_GAP = 100;
const TOP_MARGIN = 80;

const TournamentBracket: React.FC<TournamentBracketProps> = ({
  stages,
  setSelectedMatch,
  setOpenEditMatch,
  loadMatches,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const backgroundColor = isDarkMode ? theme.palette.background.default : '#f5f9ff';
  const lineColor = isDarkMode ? theme.palette.divider : '#ccc';

  const stageGroups = useMemo(() => {
    const roundOrder: Record<string, number> = {
      'Round of 128': 1,
      'Round of 64': 2,
      'Round of 32': 3,
      'Round of 16': 4,
      Quarterfinals: 5,
      'Quarter Finals': 5,
      Quarterfinal: 5,
      Semifinals: 6,
      'Semi Finals': 6,
      Semifinal: 6,
      Finals: 7,
      Final: 7,
    };

    const sortedStages = [...stages].sort((a, b) => {
      const aOrder = roundOrder[a.round] || parseInt(a.round.replace(/\D/g, '')) || 0;
      const bOrder = roundOrder[b.round] || parseInt(b.round.replace(/\D/g, '')) || 0;

      return aOrder - bOrder;
    });

    const groups: Array<{
      name: string;
      xPosition: number;
      matches: Array<{
        knockoutMatch: KnockoutMatch;
        match: Match;
        yPosition: number;
        matchIndex: number;
      }>;
    }> = [];

    sortedStages.forEach((stage, stageIndex) => {
      const matchesInGroup: Array<{
        knockoutMatch: KnockoutMatch;
        match: Match;
        yPosition: number;
        matchIndex: number;
      }> = [];

      stage.matches.forEach((knockoutMatch, matchIndex) => {
        const convertedMatch: Match = {
          id: knockoutMatch.id,
          name: `Match ${knockoutMatch.id}`,
          nextMatchId: null,
          tournamentRoundText: stage.round,
          startTime: knockoutMatch.match_date + ' ' + knockoutMatch.match_time,
          state: knockoutMatch.is_live === 1 ? 'LIVE' : 'SCHEDULED',
          participants: [
            {
              id: knockoutMatch.tournament_team_1_id,
              name: knockoutMatch.teams_1_players.map((p) => p.name).join(' / '),
            },
            {
              id: knockoutMatch.tournament_team_2_id,
              name: knockoutMatch.teams_2_players.map((p) => p.name).join(' / '),
            },
          ],
          scores:
            knockoutMatch.teams_results
              ?.map((result) => [parseInt(result.team_1) || 0, parseInt(result.team_2) || 0])
              .flat() || [],
          location: knockoutMatch.pitch_name || undefined,
          ...knockoutMatch,
        };

        matchesInGroup.push({
          knockoutMatch,
          match: convertedMatch,
          yPosition: 0,
          matchIndex,
        });
      });

      const xPosition = stageIndex * (COLUMN_WIDTH + COLUMN_GAP);

      groups.push({
        name: stage.round,
        xPosition,
        matches: matchesInGroup,
      });
    });

    for (let i = 0; i < groups.length - 1; i++) {
      const currentRound = groups[i];
      const nextRound = groups[i + 1];

      currentRound.matches.forEach((match, index) => {
        const nextMatchIndex = Math.floor(index / 2);

        if (nextMatchIndex < nextRound.matches.length) {
          const nextMatch = nextRound.matches[nextMatchIndex];
          match.match.nextMatchId = nextMatch.knockoutMatch.id;
        }
      });
    }

    if (groups.length > 0) {
      const finalRound = groups[groups.length - 1];
      if (finalRound.matches.length > 0) {
        finalRound.matches[0].yPosition = 0;
      }

      for (let i = groups.length - 2; i >= 0; i--) {
        const currentRound = groups[i];
        const nextRound = groups[i + 1];
        const matchesByNextMatch: Record<
          number,
          Array<{
            knockoutMatch: KnockoutMatch;
            match: Match;
            yPosition: number;
            matchIndex: number;
          }>
        > = {};

        currentRound.matches.forEach((match) => {
          if (match.match.nextMatchId) {
            if (!matchesByNextMatch[match.match.nextMatchId]) {
              matchesByNextMatch[match.match.nextMatchId] = [];
            }
            matchesByNextMatch[match.match.nextMatchId].push(match);
          }
        });
        Object.entries(matchesByNextMatch).forEach(([nextMatchIdStr, matches]) => {
          const nextMatchId = parseInt(nextMatchIdStr);
          const nextMatch = nextRound.matches.find((m) => m.knockoutMatch.id === nextMatchId);

          if (nextMatch) {
            matches.sort((a, b) => a.matchIndex - b.matchIndex);

            const spacingMultiplier = Math.pow(2, i);
            const baseSpacing = MATCH_CARD_HEIGHT + MATCH_VERTICAL_SPACING;
            const adjustedSpacing = baseSpacing * spacingMultiplier;

            if (matches.length === 2) {
              const centerY = nextMatch.yPosition;
              matches[0].yPosition = centerY - adjustedSpacing / 2;
              matches[1].yPosition = centerY + adjustedSpacing / 2;
            } else if (matches.length === 1) {
              matches[0].yPosition = nextMatch.yPosition;
            }
          }
        });
      }

      let minY = 0;
      groups.forEach((round) => {
        round.matches.forEach((match) => {
          minY = Math.min(minY, match.yPosition);
        });
      });
      const verticalOffset = Math.abs(minY) + MATCH_VERTICAL_SPACING;
      groups.forEach((round) => {
        round.matches.forEach((match) => {
          match.yPosition += verticalOffset;
        });
      });
    }

    return groups;
  }, [stages]);

  const matchConnections = useMemo(() => {
    const connections: Array<{
      sourceMatch: {
        id: number;
        x: number;
        y: number;
      };
      targetMatch: {
        id: number;
        x: number;
        y: number;
      };
      isUpperMatch: boolean;
    }> = [];

    for (let i = 0; i < stageGroups.length - 1; i++) {
      const currentStage = stageGroups[i];

      currentStage.matches.forEach((match) => {
        if (match.match.nextMatchId) {
          const nextStage = stageGroups[i + 1];
          const nextMatch = nextStage.matches.find(
            (m) => m.knockoutMatch.id === match.match.nextMatchId,
          );

          if (nextMatch) {
            const siblingMatches = currentStage.matches.filter(
              (m) => m.match.nextMatchId === match.match.nextMatchId,
            );
            const sourceX = currentStage.xPosition + COLUMN_WIDTH;
            const sourceY = match.yPosition + TOP_MARGIN + MATCH_CARD_HEIGHT / 2;
            const targetX = nextStage.xPosition;
            const targetY = nextMatch.yPosition + TOP_MARGIN + MATCH_CARD_HEIGHT / 2;

            siblingMatches.sort((a, b) => a.yPosition - b.yPosition);

            const isUpperMatch = siblingMatches[0]?.knockoutMatch.id === match.knockoutMatch.id;

            connections.push({
              sourceMatch: {
                id: match.knockoutMatch.id,
                x: sourceX,
                y: sourceY,
              },
              targetMatch: {
                id: nextMatch.knockoutMatch.id,
                x: targetX,
                y: targetY,
              },
              isUpperMatch,
            });
          }
        }
      });
    }

    return connections;
  }, [stageGroups]);

  const totalWidth = useMemo(() => {
    if (stageGroups.length === 0) return COLUMN_WIDTH;
    const lastStage = stageGroups[stageGroups.length - 1];

    return lastStage.xPosition + COLUMN_WIDTH + 50;
  }, [stageGroups]);

  const totalHeight = useMemo(() => {
    let maxHeight = 0;

    stageGroups.forEach((stage) => {
      if (stage.matches.length > 0) {
        const lastMatch = stage.matches.reduce(
          (max, match) => (match.yPosition > max.yPosition ? match : max),
          stage.matches[0],
        );
        const bottom = lastMatch.yPosition + MATCH_CARD_HEIGHT + TOP_MARGIN + 20;
        maxHeight = Math.max(maxHeight, bottom);
      }
    });

    return Math.max(800, maxHeight);
  }, [stageGroups]);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: totalHeight,
        overflowX: 'auto',
        overflowY: 'auto',
        padding: 3,
        backgroundColor,
        '&::-webkit-scrollbar': {
          height: '8px',
          width: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: theme.palette.divider,
          borderRadius: '4px',
        },
      }}
    >
      <Box sx={{ position: 'relative', width: totalWidth, height: totalHeight }}>
        {/* Round headers */}
        {stageGroups.map((stage, index) => (
          <Box
            key={`header-${index}`}
            sx={{
              position: 'absolute',
              left: stage.xPosition,
              top: 0,
              width: COLUMN_WIDTH,
              height: HEADER_HEIGHT,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${theme.palette.divider}`,
              zIndex: 3,
              backgroundColor,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                textAlign: 'center',
                fontWeight: 'medium',
                color: theme.palette.text.primary,
              }}
            >
              {stage.name}
            </Typography>
          </Box>
        ))}

        {matchConnections.map((connection, idx) => {
          const connectionsToSameTarget = matchConnections.filter(
            (conn) => conn.targetMatch.id === connection.targetMatch.id,
          );

          const midX =
            connection.sourceMatch.x + (connection.targetMatch.x - connection.sourceMatch.x) / 2;

          if (connectionsToSameTarget.length === 1) {
            return (
              <Box
                key={`conn-${idx}`}
                sx={{
                  position: 'absolute',
                  left: connection.sourceMatch.x,
                  top: connection.sourceMatch.y,
                  width: connection.targetMatch.x - connection.sourceMatch.x,
                  height: '2px',
                  backgroundColor: lineColor,
                  zIndex: 1,
                }}
              />
            );
          }

          const sortedConnections = [...connectionsToSameTarget].sort(
            (a, b) => a.sourceMatch.y - b.sourceMatch.y,
          );

          const isUpper = connection.sourceMatch.y === sortedConnections[0].sourceMatch.y;

          if (isUpper) {
            return (
              <React.Fragment key={`conn-complex-${idx}`}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: connection.sourceMatch.x,
                    top: connection.sourceMatch.y,
                    width: midX - connection.sourceMatch.x,
                    height: '2px',
                    backgroundColor: lineColor,
                    zIndex: 1,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: midX,
                    top: sortedConnections[0].sourceMatch.y,
                    width: '2px',
                    height: sortedConnections[1].sourceMatch.y - sortedConnections[0].sourceMatch.y,
                    backgroundColor: lineColor,
                    zIndex: 1,
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: midX,
                    top: connection.targetMatch.y,
                    width: connection.targetMatch.x - midX,
                    height: '2px',
                    backgroundColor: lineColor,
                    zIndex: 1,
                  }}
                />
              </React.Fragment>
            );
          } else {
            return (
              <Box
                key={`conn-half-${idx}`}
                sx={{
                  position: 'absolute',
                  left: connection.sourceMatch.x,
                  top: connection.sourceMatch.y,
                  width: midX - connection.sourceMatch.x,
                  height: '2px',
                  backgroundColor: lineColor,
                  zIndex: 1,
                }}
              />
            );
          }
        })}

        {stageGroups.map((stage, stageIndex) => (
          <React.Fragment key={`stage-${stageIndex}`}>
            {stage.matches.map((matchInfo) => (
              <Box
                key={`match-${matchInfo.knockoutMatch.id}`}
                sx={{
                  position: 'absolute',
                  left: stage.xPosition,
                  top: matchInfo.yPosition + TOP_MARGIN,
                  width: COLUMN_WIDTH,
                  height: MATCH_CARD_HEIGHT,
                  zIndex: 2,
                }}
              >
                <MatchCard
                  match={matchInfo.match}
                  setSelectedMatch={setSelectedMatch}
                  setOpenEditMatch={setOpenEditMatch}
                  loadMatches={loadMatches}
                />
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default TournamentBracket;
